import { Component, Element, Host, Prop, h } from '@stencil/core';

import { AutoCheckState } from '../../../store/store.autocheck';
import { ConsumerMarketConfidenceState, initialState as consumerMarketConfidenceInitialState } from '../../../store/store.consumer-market-confidence';
import { CounterofferState, initialState as counterofferInitialState } from '../../../store/store.counteroffer';
import { KelleyBuysYourCarOfferState, initialState as kelleyBuysYourCarOfferInitialState } from '../../../store/store.kelley-buys-your-car-offer';
import { OfferState } from '../../../store/store.offer';
import { trackOfferCertificateHeader } from '../../../tracking/tracking.offer';
import { defaultInProgressVehicleState } from '../../../utils/utils.vehicle';

@Component({
  tag: 'offer-certificate',
  styleUrl: 'offer-certificate.css',
  shadow: true,
})
export class OfferCertificate {
  @Element() el: HTMLElement;

  @Prop() autoCheck: AutoCheckState;

  @Prop() customer = { email: '', firstName: '', lastName: '', phone: '', zip: '' };
  @Prop() isAdmin: boolean = false;

  @Prop() dealer = {
    allocated: [],
    dealerOffers: {},
    buyingCenters: [],
    isOfficeClosed: false,
    name: '',
    numAssignableDealers: 0,
    dealerAppointmentInfo: null,
    isAcceptOrCounterofferON: false,
  };

  @Prop() offer: OfferState;

  @Prop() vehicle = {
    accidents: false,
    auctionedRecently: false,
    color: '',
    description: '',
    drivetrain: '',
    engine: '',
    fullName: '',
    hasReplacement: false,
    hasTwoSetsOfKeys: true,
    imageUrl: '',
    offerOptions: [],
    isAuctionedLast45: false,
    isRental: false,
    make: '',
    makeId: 0,
    mileage: 0,
    model: '',
    modelId: 0,
    originalOwner: false,
    rentalVehicle: false,
    replacementName: '',
    replacementText: '',
    replacementTypes: '',
    replacementVehicleYearMax: 2023,
    replacementVehicleYearMin: 2018,
    transmission: '',
    trim: '',
    trimId: 0,
    vin: '',
    year: '',
    yearId: 0,
    yearMakeModel: '',
    inProgressVehicle: defaultInProgressVehicleState,
  };

  @Prop() optimizely = {
    icocertenhancementsOptimizely: false,
    isConsumerMarketConfidenceOn: false,
    isAppleWalletPassEnabled: false,
  };

  @Prop() theme: string;

  @Prop() acceptOrCounteroffer: CounterofferState = counterofferInitialState;
  @Prop() kelleyBuysYourCarOffer: KelleyBuysYourCarOfferState = kelleyBuysYourCarOfferInitialState;
  @Prop() consumerMarketConfidence: ConsumerMarketConfidenceState = consumerMarketConfidenceInitialState;

  isValidOffer() {
    const { amount = 0, isExpired = false, isIneligibleOffer = false, isOfferUnderReview = false } = this.offer;
    return amount > 0 && !isIneligibleOffer && !isExpired && !isOfferUnderReview;
  }

  isExpiredOffer() {
    const { isExpired = false } = this.offer;
    return isExpired;
  }

  isNeedsReviewOffer() {
    const { isOfferUnderReview = false, isExpired = false } = this.offer;
    return isOfferUnderReview && !isExpired;
  }

  isNotEligibleOffer() {
    const { isIneligibleOffer, isExpired, enableMileageIneligibleEditing } = this.offer;
    return isIneligibleOffer && !isExpired && !enableMileageIneligibleEditing;
  }

  isValidDrOrCoBrandingDrOffer() {
    const { isDrOffer, isCobrandingDRUnassigned, isCobrandingDRAssigned } = this.offer;
    return this.isValidOffer() && (isDrOffer || isCobrandingDRUnassigned || isCobrandingDRAssigned);
  }

  // this check will be used in the offer certificate valid check once DR is enabled
  isNotSpecialOfferCodeOffer() {
    const { isDrOffer, isCobrandingDRUnassigned, isCobrandingDRAssigned } = this.offer;
    return !isDrOffer && !isCobrandingDRUnassigned && !isCobrandingDRAssigned;
  }

  isIneligibleOfferByMileage() {
    const { enableMileageIneligibleEditing } = this.offer;
    return enableMileageIneligibleEditing;
  }

  isInProgressOffer() {
    const { prospectId, offerId } = this.offer;
    return !offerId && prospectId;
  }

  isInspectionOffer() {
    const { isInspectionNeeded, isExpired } = this.offer;
    return isInspectionNeeded && !isExpired;
  }

  showVehicleDetailDialog(event: MouseEvent) {
    event.preventDefault();
    this.el.querySelector<HTMLIcoModalElement>('ico-modal#offer-details').open();
    trackOfferCertificateHeader('review_vehicle_details');
  }

  render() {
    const { autoCheck, customer, dealer, offer, vehicle, optimizely, isAdmin, showVehicleDetailDialog, theme, consumerMarketConfidence } = this;
    const sharedProps = { autoCheck, customer, dealer, offer, vehicle, optimizely, showVehicleDetailDialog, theme, consumerMarketConfidence };
    const { isKelleyBuysYourCar } = this.offer;

    return (
      <Host>
        <div class={`${this.theme}-theme`}>
          {this.isInProgressOffer() && (
            <offer-certificate-inprogress
              inProgressBtnText={offer.inProgressBtnText}
              prospectId={this.offer.prospectId}
              customer={this.customer}
              vehicle={this.vehicle.inProgressVehicle}
            />
          )}
          {/* Eligible (successful) offer certificate for Unassigned or Assigned Offers with no special offer codes*/}
          {this.isValidOffer() && this.isNotSpecialOfferCodeOffer() && (
            <offer-certificate-valid {...sharedProps} isAdmin={isAdmin} acceptOrCounteroffer={this.acceptOrCounteroffer} kelleyBuysYourCarOffer={this.kelleyBuysYourCarOffer} />
          )}

          {/* Eligible (successful) offer certificate for Unassigned or Assigned DR Offers*/}
          {this.isValidDrOrCoBrandingDrOffer() && <offer-certificate-valid-dr {...sharedProps} isAdmin={isAdmin} acceptOrCounteroffer={this.acceptOrCounteroffer} />}

          {/* Expired handler */}
          {(this.isExpiredOffer() || this.isIneligibleOfferByMileage()) && <offer-simplified-resubmit {...sharedProps} />}

          {/* Under review certificate */}
          {this.isNeedsReviewOffer() && !(isKelleyBuysYourCar && this.isInspectionOffer()) && (
            <offer-refresh offerId={this.offer.offerId} isActive={this.offer.isFurtherReviewNeeded}>
              <offer-certificate-review {...sharedProps} />
            </offer-refresh>
          )}
          {/*
              valuation operations team
              - further review reasons
          */}
          {/* NOT valid */}
          {/* Ineligible offer certificate */}
          {(this.isNotEligibleOffer() || (isKelleyBuysYourCar && this.isInspectionOffer())) && <offer-certificate-ineligible {...sharedProps} />}
        </div>
      </Host>
    );
  }
}
